/**
 * GraphicSIMS — NewBlue NEVCO Pro Video Training Simulation
 * Main entry point
 *
 * Three modes determined by auth:
 *   Enterprise → Landing → Sessions → Videos/Lessons → Simulator
 *   Student/Teacher/Admin → Dashboard → Lesson Runner / Free Explore
 *   Unauthenticated → Enterprise (dev default)
 */

import './styles/fonts.css';
import './styles/main.css';
import './styles/properties.css';
import './styles/template-renderer.css';
import './styles/menus.css';
import './styles/enterprise.css';
import './styles/student.css';
import './styles/videoboard.css';
import './styles/enterprise-lesson.css';
import './styles/admin.css';

import { initToolbar } from './ui/toolbar';
import { initMenuSystem } from './ui/menuSystem';
import { initContextMenu } from './ui/contextMenu';
import { initLibrary } from './ui/library';
import { initProject } from './ui/project';
import { initProperties } from './ui/properties';
import { initPreview } from './ui/preview';
import { initProgram, openVideoBoardOnBoot } from './ui/program';
import { initShotLauncher } from './ui/shotLauncher';
import { initDataLinkDialog } from './ui/dataLinkDialog';
import { initDataLinker } from './engine/dataLinker';
import { initKeyboardManager } from './engine/keyboardManager';
import { initPanelResize } from './engine/panelResize';
import { initDragDrop } from './engine/dragDropManager';
import { initPanelDocking } from './engine/panelDocking';
import { preloadSvgTemplates } from './engine/templateRenderer';
import { initUnloadBroadcast } from './engine/videoBoardChannel';

import { registerRoute, startRouter } from './router';
import { checkAuthAndRoute } from './router/routeGuard';
import type { AuthResult } from './router/routeGuard';
import { renderLanding } from './pages/landing';
import { renderSessions } from './pages/sessions';
import { renderVideos } from './pages/videos';
import { renderLessons } from './pages/lessons';
import { renderDashboard } from './pages/student/dashboard';
import { renderLessonRunner, removeLessonRunner } from './pages/student/lessonRunner';
import { renderQuizPlayer } from './pages/student/quizPlayer';
import { renderVideoBoard } from './pages/videoBoard';
import { renderEnterpriseLessonRunner, removeEnterpriseLessonRunner } from './pages/enterprise/lessonRunner';
import { renderAdminBugs } from './pages/admin/bugs';

let simInitialized = false;
let skipVideoBoard = false;

function initSim(opts?: { skipVideoBoard?: boolean }): void {
  if (opts?.skipVideoBoard) skipVideoBoard = true;
  if (simInitialized) return;
  simInitialized = true;

  console.info('GraphicSIMS v1.0.0 — NEVCO Pro Video Simulation');

  // Name this window so the video board can focus it via window.open(url, 'nevco-sim')
  window.name = 'nevco-sim';

  preloadSvgTemplates();
  initToolbar();
  initMenuSystem();
  initLibrary();
  initProject();
  initProperties();
  initPreview();
  initProgram();
  initShotLauncher();
  initDataLinkDialog();
  initDataLinker();
  initContextMenu();
  initKeyboardManager();
  initPanelResize();
  initDragDrop();
  initPanelDocking();

  // Open video board tab on boot — pre-buffers video for zero-delay sync
  // Skip during enterprise lessons (user opens it manually as part of the lesson)
  if (!skipVideoBoard) {
    openVideoBoardOnBoot();
  }

  // Reset video board when sim tab closes or refreshes
  initUnloadBroadcast();
}

function hideVideoBoard(): void {
  const vb = document.getElementById('videoboard-container');
  if (vb) vb.style.display = 'none';
}

function showEnterprise(): void {
  document.getElementById('enterprise-container')!.style.display = '';
  document.getElementById('student-container')!.style.display = 'none';
  document.getElementById('sim-container')!.style.display = 'none';
  hideVideoBoard();
}

function showStudent(): void {
  document.getElementById('enterprise-container')!.style.display = 'none';
  document.getElementById('student-container')!.style.display = '';
  document.getElementById('sim-container')!.style.display = 'none';
  hideVideoBoard();
}

function showSim(opts?: { skipVideoBoard?: boolean }): void {
  document.getElementById('enterprise-container')!.style.display = 'none';
  document.getElementById('student-container')!.style.display = 'none';
  document.getElementById('sim-container')!.style.display = '';
  hideVideoBoard();
  initSim(opts);
}

function showVideoBoard(): void {
  document.getElementById('enterprise-container')!.style.display = 'none';
  document.getElementById('student-container')!.style.display = 'none';
  document.getElementById('sim-container')!.style.display = 'none';
  const vb = document.getElementById('videoboard-container');
  if (vb) vb.style.display = '';
}

document.addEventListener('DOMContentLoaded', async () => {
  const auth: AuthResult = await checkAuthAndRoute();

  if (auth.mode === 'student') {
    // Student/teacher/admin flow: register student routes and start router
    const stuContainer = document.getElementById('student-container')!;

    registerRoute('/', () => {
      removeLessonRunner();
      showStudent();
      renderDashboard(stuContainer, auth);
    });

    registerRoute('/dashboard', () => {
      removeLessonRunner();
      showStudent();
      renderDashboard(stuContainer, auth);
    });

    registerRoute('/lesson/:id', (params) => {
      const lessonId = params?.id;
      if (!lessonId) {
        showStudent();
        renderDashboard(stuContainer, auth);
        return;
      }
      showSim();
      renderLessonRunner(lessonId, auth);
    });

    registerRoute('/quiz/:id', (params) => {
      const quizId = params?.id;
      if (!quizId) {
        showStudent();
        renderDashboard(stuContainer, auth);
        return;
      }
      showStudent();
      renderQuizPlayer(stuContainer, quizId);
    });

    registerRoute('/sim', () => {
      removeLessonRunner();
      showSim();
    });

    registerRoute('/videoboard', () => {
      showVideoBoard();
      renderVideoBoard(document.getElementById('videoboard-container')!);
    });

    registerRoute('/admin/bugs', () => {
      removeLessonRunner();
      showStudent();
      renderAdminBugs(stuContainer);
    });

    startRouter();
    return;
  }

  // Enterprise flow: register routes and start router
  const entContainer = document.getElementById('enterprise-container')!;

  registerRoute('/', () => {
    showEnterprise();
    renderLanding(entContainer);
  });

  registerRoute('/sessions', () => {
    showEnterprise();
    renderSessions(entContainer);
  });

  registerRoute('/videos', () => {
    showEnterprise();
    renderVideos(entContainer);
  });

  registerRoute('/lessons', () => {
    removeEnterpriseLessonRunner();
    showEnterprise();
    renderLessons(entContainer);
  });

  registerRoute('/enterprise-lesson/:id', (params) => {
    const lessonId = params?.id;
    if (!lessonId) {
      showEnterprise();
      renderLessons(entContainer);
      return;
    }
    removeEnterpriseLessonRunner();
    showSim({ skipVideoBoard: true });
    renderEnterpriseLessonRunner(lessonId);
  });

  registerRoute('/sim', () => {
    removeEnterpriseLessonRunner();
    showSim();
  });

  registerRoute('/videoboard', () => {
    showVideoBoard();
    renderVideoBoard(document.getElementById('videoboard-container')!);
  });

  registerRoute('/admin/bugs', () => {
    removeEnterpriseLessonRunner();
    showEnterprise();
    renderAdminBugs(entContainer);
  });

  startRouter();
});
